import { useLayoutEffect } from "react";
import { useShallow } from "zustand/shallow";
import { useBoundStore } from "@/app/store";
import { NavbarProps } from "@/shared/components/layout/Navbar.mobile";
import { FabProps } from "@/shared/components/layout/PageLayout";

interface UsePageLayoutConfig {
  navbar?: NavbarProps;
  fab?: FabProps;
  showBottomNavigation?: boolean;
  className?: string;
  resizeViewportOnKeyboardOpen?: boolean;
  onScroll?: (event: React.UIEvent<HTMLElement>) => void;
  animationKey?: string;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Hook to configure the persistent page layout from within a route component.
 * Uses useLayoutEffect for synchronous updates before paint to prevent flicker.
 * The layout will be updated in the global store without re-rendering the layout itself.
 *
 * @example
 * export function MyRoute() {
 *   usePageLayout({
 *     navbar: { title: "My Page", hideBack: false },
 *     fab: { icon: <Icon />, onPress: handleCreate },
 *     showBottomNavigation: true,
 *   });
 *
 *   return <div>Page content</div>;
 * }
 */
export function usePageLayout(config: UsePageLayoutConfig = {}) {
  const { set: setPageLayout } = useBoundStore(useShallow((state) => ({ set: state.pageLayout.set })));

  useLayoutEffect(() => {
    setPageLayout(config);
  }, [setPageLayout, config]);
}
